import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DishLibrary } from '../components/DishLibrary';
import { FixedPurchaseSettings } from '../components/FixedPurchaseSettings';
import {
  AlertIcon,
  BookIcon,
  CalendarIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  LinkIcon,
  RefreshIcon,
  UtensilsIcon,
} from '../components/Icons';
import { Modal } from '../components/Modal';
import { PurchaseSummary } from '../components/PurchaseSummary';
import { Toast, type ToastData } from '../components/Toast';
import { WeekPlanner } from '../components/WeekPlanner';
import { WeeklyExtraPurchases } from '../components/WeeklyExtraPurchases';
import { WeeklyFixedPurchaseAdjustments } from '../components/WeeklyFixedPurchaseAdjustments';
import { WeeklyServiceSettings } from '../components/WeeklyServiceSettings';
import {
  confirmPlan,
  createShareLink,
  fetchExportData,
  getBootstrap,
  readFragmentToken,
  savePlan,
  saveFixedPurchaseOverrides,
} from '../lib/api';
import {
  aggregatePurchaseByDay,
  buildDailyWechatText,
  buildWechatText,
  ingredientUnitWarnings,
  prunePurchaseOverrides,
} from '../lib/calculation';
import {
  formatWeekRange,
  isCalendarConfigured,
  mondayOf,
  nextWeekStart,
  shiftWeek,
  toIsoDate,
} from '../lib/date';
import { downloadPurchaseExcel } from '../lib/export';
import { DEFAULT_DAILY_PURCHASE_DEFAULTS } from '../lib/ingredient-categories';
import { isValidPurchaseQuantity } from '../lib/purchase-quantity';
import type {
  ConfirmedSnapshot,
  DailyPurchaseDefault,
  Dish,
  FixedPurchaseOverride,
  FixedPurchaseRule,
  IngredientCatalogItem,
  WechatPurchaseMessage,
  WeeklyPlan,
} from '../types';

type SaveState = 'saved' | 'saving' | 'error';

const WEEKLY_FIXED_PURCHASE_DISH_IDS = [
  'dish_menu_other_egg',
  'dish_menu_other_milk',
  'dish_menu_other_old_ginger',
];

function catalogFromDishes(dishes: Dish[]): IngredientCatalogItem[] {
  return Array.from(
    new Map(
      dishes.flatMap((dish) => dish.ingredients).flatMap((item) => item.id ? [[item.id, {
        id: item.id,
        name: item.name,
        unit: item.unit,
        ingredientCategory: item.ingredientCategory ?? '其他',
      } satisfies IngredientCatalogItem] as const] : []),
    ).values(),
  );
}

function makeLocalSnapshot(
  plan: WeeklyPlan,
  dishes: Dish[],
  kitchenName: string,
  dailyPurchaseDefaults: DailyPurchaseDefault[],
): ConfirmedSnapshot {
  const byId = new Map(dishes.map((dish) => [dish.id, dish]));
  const aggregation = aggregatePurchaseByDay(plan, dishes, dailyPurchaseDefaults);
  const selections = plan.days.flatMap((day) => (day.mealService
    ? day.mealService.breakfast || day.mealService.lunch
    : day.serviceEnabled) ? day.selections.filter((selection) => (
    day.mealService?.[selection.meal] ?? day.serviceEnabled
  )).map((selection) => {
    const dish = byId.get(selection.dishId);
    return {
      date: day.date,
      meal: selection.meal,
      dishName: selection.recipeSnapshot?.dishName ?? dish?.name ?? '已停用菜品',
      batchCount: selection.batchCount,
      ingredients: (selection.recipeSnapshot?.ingredients ?? dish?.ingredients ?? []).map((item) => ({ ...item })),
    };
  }) : []);
  const extraSelections = (plan.extraSelections ?? []).map((selection) => {
    const dish = byId.get(selection.dishId);
    return {
      dishName: selection.recipeSnapshot?.dishName ?? dish?.name ?? '已停用菜品',
      batchCount: selection.batchCount,
      ingredients: (selection.recipeSnapshot?.ingredients ?? dish?.ingredients ?? []).map((item) => ({ ...item })),
    };
  });
  return {
    planId: plan.id ?? plan.weekStart,
    kitchenName,
    weekStart: plan.weekStart,
    weekEnd: plan.weekEnd,
    version: Math.max(1, plan.version + 1),
    dailyPurchaseGroups: aggregation.dailyPurchaseGroups,
    extraPurchaseItems: aggregation.extraPurchaseItems,
    purchaseItems: aggregation.purchaseItems,
    selections,
    extraSelections,
    extraItems: (plan.extraItems ?? []).map((item) => ({ ...item })),
    wechatText: buildDailyWechatText(
      kitchenName,
      formatWeekRange(plan.weekStart, plan.weekEnd),
      aggregation.dailyPurchaseGroups,
      aggregation.extraPurchaseItems,
    ),
  };
}

function mergeSnapshot(server: ConfirmedSnapshot, local: ConfirmedSnapshot): ConfirmedSnapshot {
  const serverHasDailyShape = server.dailyPurchaseGroups !== undefined || server.extraPurchaseItems !== undefined;
  const dailyPurchaseGroups = serverHasDailyShape ? (server.dailyPurchaseGroups ?? []) : local.dailyPurchaseGroups;
  const extraPurchaseItems = serverHasDailyShape ? (server.extraPurchaseItems ?? []) : local.extraPurchaseItems;
  const groupedItems = [
    ...(dailyPurchaseGroups ?? []).flatMap((group) => group.items),
    ...(extraPurchaseItems ?? []),
  ];
  const purchaseItems = groupedItems.length
    ? groupedItems
    : server.purchaseItems.length ? server.purchaseItems : local.purchaseItems;
  const kitchenName = server.kitchenName || local.kitchenName;
  const weekStart = server.weekStart || local.weekStart;
  const weekEnd = server.weekEnd || local.weekEnd;
  const mergedWechatText = server.wechatText
    || server.wechatMessages?.map((message) => message.text).join('\n\n')
    || local.wechatText;
  return {
    ...local,
    ...server,
    planId: server.planId || local.planId,
    kitchenName,
    weekStart,
    weekEnd,
    dailyPurchaseGroups,
    extraPurchaseItems,
    purchaseItems,
    selections: server.selections.length ? server.selections : local.selections,
    extraSelections: server.extraSelections?.length ? server.extraSelections : local.extraSelections,
    // A confirmed server version is immutable. Never recalculate its copy from
    // current dishes or daily groups; the local text is only a last-resort
    // compatibility fallback for servers that returned no copy at all.
    wechatText: mergedWechatText,
  };
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const element = document.createElement('textarea');
  element.value = text;
  element.style.position = 'fixed';
  element.style.opacity = '0';
  document.body.appendChild(element);
  element.select();
  document.execCommand('copy');
  element.remove();
}

export function EditorPage() {
  const token = useMemo(() => readFragmentToken('access'), []);
  const [week, setWeek] = useState(nextWeekStart);
  const [plan, setPlan] = useState<WeeklyPlan | null>(null);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [ingredients, setIngredients] = useState<IngredientCatalogItem[]>([]);
  const [dailyPurchaseDefaults, setDailyPurchaseDefaults] = useState<DailyPurchaseDefault[]>(
    () => DEFAULT_DAILY_PURCHASE_DEFAULTS.map((item) => ({ ...item })),
  );
  const [fixedPurchaseRules, setFixedPurchaseRules] = useState<FixedPurchaseRule[]>([]);
  const [fixedPurchaseDefaultEffectiveFromWeek, setFixedPurchaseDefaultEffectiveFromWeek] = useState('');
  const [kitchenName, setKitchenName] = useState('龙庭公司厨房');
  const [snapshot, setSnapshot] = useState<ConfirmedSnapshot | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [changeSequence, setChangeSequence] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [actionBusy, setActionBusy] = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [serviceSettingsOpen, setServiceSettingsOpen] = useState(false);
  const [fixedPurchaseSettingsOpen, setFixedPurchaseSettingsOpen] = useState(false);
  const [weekFixedAdjustmentsOpen, setWeekFixedAdjustmentsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [toast, setToast] = useState<ToastData | null>(null);
  const planRef = useRef<WeeklyPlan | null>(null);
  const currentSequence = useRef(0);
  const savedSequence = useRef(0);
  const requestSequence = useRef(0);

  const notify = useCallback((message: string, tone: ToastData['tone'] = 'info') => {
    setToast({ id: Date.now(), message, tone });
  }, []);

  const setPlanWithoutChange = (value: WeeklyPlan | null) => {
    planRef.current = value;
    setPlan(value);
  };

  const loadWeek = useCallback(async (target: string) => {
    if (!token) {
      setLoading(false);
      return;
    }
    const requestId = ++requestSequence.current;
    setLoading(true);
    setLoadError('');
    try {
      const result = await getBootstrap(token, target);
      if (requestSequence.current !== requestId) return;
      setKitchenName(result.kitchenName);
      setDishes(result.dishes);
      setIngredients(result.ingredients ?? catalogFromDishes(result.dishes));
      setDailyPurchaseDefaults(result.dailyPurchaseDefaults);
      setFixedPurchaseRules(result.fixedPurchaseRules ?? []);
      setFixedPurchaseDefaultEffectiveFromWeek(result.fixedPurchaseDefaultEffectiveFromWeek ?? result.plan.weekStart);
      setPlanWithoutChange(result.plan);
      setSnapshot(result.latestSnapshot);
      setWeek(result.plan.weekStart);
      currentSequence.current = 0;
      savedSequence.current = 0;
      setChangeSequence(0);
      setSaveState('saved');
      if (!result.latestSnapshot && result.plan.status === 'confirmed' && result.plan.id) {
        try {
          const confirmed = await fetchExportData(token, result.plan.id);
          if (requestSequence.current === requestId) setSnapshot(confirmed);
        } catch {
          // The plan remains usable even if an older export version cannot be loaded.
        }
      }
    } catch (reason) {
      if (requestSequence.current !== requestId) return;
      setLoadError(reason instanceof Error ? reason.message : '计划加载失败，请重试');
      setPlanWithoutChange(null);
    } finally {
      if (requestSequence.current === requestId) setLoading(false);
    }
  }, [token]);

  useEffect(() => { void loadWeek(week); }, [loadWeek]);

  useEffect(() => {
    if (!changeSequence || !planRef.current) return;
    const sequence = changeSequence;
    const timer = window.setTimeout(async () => {
      const current = planRef.current;
      if (!current) return;
      const draft = prunePurchaseOverrides(current, dishes, dailyPurchaseDefaults);
      if (draft !== current) setPlanWithoutChange(draft);
      setSaveState('saving');
      try {
        const saved = await savePlan(token, draft);
        if (currentSequence.current === sequence) {
          savedSequence.current = sequence;
          setPlanWithoutChange(saved);
          setSaveState('saved');
        }
      } catch (reason) {
        if (currentSequence.current === sequence) {
          setSaveState('error');
          notify(reason instanceof Error ? reason.message : '自动保存失败，请重试', 'error');
        }
      }
    }, 700);
    return () => window.clearTimeout(timer);
  }, [changeSequence, dailyPurchaseDefaults, dishes, notify, token]);

  const changePlan = (next: WeeklyPlan) => {
    const sanitized = prunePurchaseOverrides(next, dishes, dailyPurchaseDefaults);
    setPlanWithoutChange(sanitized);
    const sequence = currentSequence.current + 1;
    currentSequence.current = sequence;
    setChangeSequence(sequence);
    setSaveState('saving');
  };

  const navigateWeek = async (target: string) => {
    if (target === week || loading) return;
    const current = planRef.current;
    if (current && currentSequence.current > savedSequence.current) {
      try {
        setSaveState('saving');
        await savePlan(token, prunePurchaseOverrides(current, dishes, dailyPurchaseDefaults));
      } catch (reason) {
        notify(reason instanceof Error ? reason.message : '当前周保存失败，暂未切换', 'error');
        setSaveState('error');
        return;
      }
    }
    setWeek(target);
    void loadWeek(target);
  };

  const aggregation = useMemo(() => plan ? aggregatePurchaseByDay(plan, dishes, dailyPurchaseDefaults) : {
    dailyPurchaseGroups: [],
    extraPurchaseItems: [],
    purchaseItems: [],
  }, [plan, dishes, dailyPurchaseDefaults]);
  const items = aggregation.purchaseItems;
  const warnings = useMemo(() => ingredientUnitWarnings(items), [items]);
  // The server keeps last_confirmed_version on a draft. A few historical API
  // payloads can pair an unversioned draft (version 0) with a snapshot, so do
  // not hide those immutable outputs unless this draft is actually based on
  // that confirmed version.
  const outputsStale = Boolean(
    snapshot
    && plan?.status === 'draft'
    && plan.version >= snapshot.version,
  );

  const overrideQuantity = (key: string, quantity: string) => {
    if (!plan) return;
    if (key.split('\u0000').length >= 3) {
      changePlan({
        ...plan,
        status: 'draft',
        dailyPurchaseOverrides: { ...(plan.dailyPurchaseOverrides ?? {}), [key]: quantity },
      });
      return;
    }
    changePlan({
      ...plan,
      status: 'draft',
      purchaseOverrides: { ...plan.purchaseOverrides, [key]: quantity },
    });
  };

  const resetOverride = (key: string) => {
    if (!plan) return;
    if (key.split('\u0000').length >= 3) {
      const dailyPurchaseOverrides = { ...(plan.dailyPurchaseOverrides ?? {}) };
      delete dailyPurchaseOverrides[key];
      changePlan({
        ...plan,
        status: 'draft',
        dailyPurchaseOverrides,
      });
      return;
    }
    const purchaseOverrides = { ...plan.purchaseOverrides };
    delete purchaseOverrides[key];
    changePlan({
      ...plan,
      status: 'draft',
      purchaseOverrides,
    });
  };

  const confirm = async () => {
    const currentPlan = planRef.current;
    if (!currentPlan || items.length === 0) return;
    const current = prunePurchaseOverrides(currentPlan, dishes, dailyPurchaseDefaults);
    const overridesPruned = current !== currentPlan;
    if (overridesPruned) setPlanWithoutChange(current);
    if (items.some((item) => !isValidPurchaseQuantity(item.finalQuantity, item.unit))) {
      notify('采购数量格式不正确；个、只、件等计数单位必须填写整数', 'error');
      return;
    }
    setConfirming(true);
    try {
      let saved = current;
      if (overridesPruned || currentSequence.current > savedSequence.current || saveState !== 'saved') {
        saved = await savePlan(token, current);
        setPlanWithoutChange(saved);
        savedSequence.current = currentSequence.current;
        setSaveState('saved');
      }
      const local = makeLocalSnapshot(saved, dishes, kitchenName, dailyPurchaseDefaults);
      const server = await confirmPlan(token, saved);
      const confirmed = mergeSnapshot(server, local);
      setSnapshot(confirmed);
      setPlanWithoutChange({ ...saved, id: confirmed.planId || saved.id, status: 'confirmed', version: confirmed.version });
      notify(`第 ${confirmed.version} 版采购单已生成`, 'success');
    } catch (reason) {
      notify(reason instanceof Error ? reason.message : '确认失败，请稍后重试', 'error');
    } finally {
      setConfirming(false);
    }
  };

  const copyWechat = async (message?: WechatPurchaseMessage) => {
    if (!snapshot || outputsStale) {
      if (outputsStale) notify('采购内容已变化，请重新确认后再复制消息', 'error');
      return;
    }
    try {
      const text = message?.text ?? (
        snapshot.wechatText || (snapshot.dailyPurchaseGroups !== undefined
          ? buildDailyWechatText(
              snapshot.kitchenName,
              formatWeekRange(snapshot.weekStart, snapshot.weekEnd),
              snapshot.dailyPurchaseGroups,
              snapshot.extraPurchaseItems ?? [],
            )
          : buildWechatText(snapshot.kitchenName, formatWeekRange(snapshot.weekStart, snapshot.weekEnd), snapshot.purchaseItems))
      );
      await copyText(text);
      notify(message ? `${message.title}消息已复制` : '历史微信采购文案已复制', 'success');
    } catch {
      notify('复制失败，请在浏览器设置中允许剪贴板权限', 'error');
    }
  };

  const downloadExcel = async () => {
    if (!snapshot || outputsStale) {
      if (outputsStale) notify('采购内容已变化，请重新确认后再下载 Excel', 'error');
      return;
    }
    setActionBusy('excel');
    try {
      let data = snapshot;
      try {
        data = mergeSnapshot(await fetchExportData(token, snapshot.planId), snapshot);
      } catch {
        // The confirmed snapshot already contains the same normalized export payload.
      }
      await downloadPurchaseExcel(data);
      notify('Excel 采购清单已下载', 'success');
    } catch (reason) {
      notify(reason instanceof Error ? reason.message : 'Excel 生成失败，请重试', 'error');
    } finally {
      setActionBusy('');
    }
  };

  const share = async () => {
    if (!snapshot || outputsStale) {
      if (outputsStale) notify('采购内容已变化，请重新确认后再分享', 'error');
      return;
    }
    setActionBusy('share');
    try {
      const result = await createShareLink(token, snapshot.planId);
      const url = result.url || (result.shareToken ? `${window.location.origin}/order#share=${encodeURIComponent(result.shareToken)}` : '');
      if (!url) throw new Error('服务器未返回分享链接');
      setShareUrl(url);
      setShareOpen(true);
    } catch (reason) {
      notify(reason instanceof Error ? reason.message : '分享链接生成失败', 'error');
    } finally {
      setActionBusy('');
    }
  };

  const applyFixedPurchaseOverrides = async (overrides: FixedPurchaseOverride[]) => {
    if (!plan) return;
    const result = await saveFixedPurchaseOverrides(token, plan.weekStart, overrides);
    const current = planRef.current ?? plan;
    setPlanWithoutChange({
      ...current,
      id: result.planId || current.id,
      status: 'draft',
      fixedPurchaseOverrides: result.overrides,
      resolvedFixedPurchases: result.resolvedFixedPurchases,
      fixedPurchaseRulesRevision: result.rulesRevision,
    });
    setSaveState('saved');
    notify('本周固定采购已保存，只影响这一周', 'success');
  };

  if (!token) {
    return (
      <main className="editor-shell access-page">
        <section className="access-error"><span><AlertIcon size={30} /></span><h1>私密链接不完整</h1><p>此工作台不使用账号密码，请从厨房负责人发送的完整私密链接进入。</p><a className="button button--ghost" href="/">返回产品首页</a></section>
      </main>
    );
  }

  return (
    <div className="editor-app">
      <header className="app-header">
        <div className="app-header__inner shell">
          <a className="brand" href="/" aria-label="龙庭备菜助手首页"><span className="brand__mark"><UtensilsIcon size={21} /></span><span><strong>龙庭备菜助手</strong><small>{kitchenName}</small></span></a>
          <nav className="app-nav" aria-label="主要导航"><span className="is-active"><CalendarIcon size={18} />周计划</span><button type="button" onClick={() => setLibraryOpen(true)}><BookIcon size={18} />菜品库</button></nav>
          <button className="button button--soft button--small app-header__library" type="button" onClick={() => setLibraryOpen(true)}><BookIcon size={17} /> 菜品库</button>
        </div>
      </header>

      <main className="app-main shell">
        <section className="page-heading">
          <div><p className="eyebrow">WEEKLY PREPARATION</p><h1>下周备菜计划</h1><p>按天选菜，食材用量会自动汇总，无需再手写计算。</p></div>
          <div className="week-picker">
            <button className="icon-button" type="button" onClick={() => navigateWeek(shiftWeek(week, -1))} aria-label="上一周"><ChevronLeftIcon /></button>
            <div><strong>{plan ? formatWeekRange(plan.weekStart, plan.weekEnd) : '正在加载'}</strong><span>{week === nextWeekStart() ? '下周' : week === toIsoDate(mondayOf(new Date())) ? '本周' : '历史 / 未来周'}</span></div>
            <button className="icon-button" type="button" onClick={() => navigateWeek(shiftWeek(week, 1))} aria-label="下一周"><ChevronRightIcon /></button>
          </div>
          <div className="quick-weeks"><button type="button" onClick={() => navigateWeek(toIsoDate(mondayOf(new Date())))}>本周</button><button type="button" className={week === nextWeekStart() ? 'is-active' : ''} onClick={() => navigateWeek(nextWeekStart())}>下周</button></div>
        </section>

        {!loading && plan && !(plan.calendarConfigured ?? isCalendarConfigured(plan.weekStart)) && (
          <div className="calendar-warning"><AlertIcon size={19} /><div><strong>{plan.weekStart.slice(0, 4)} 年节假日数据尚未更新</strong><p>当前暂按周一至周五供餐，可在“本周供餐设置”中按早餐和中午调整。</p></div></div>
        )}

        {loading && <div className="page-loading page-loading--panel"><span className="spinner" /><p>正在准备这一周的菜单…</p></div>}
        {loadError && (
          <div className="load-error"><AlertIcon size={28} /><h2>周计划加载失败</h2><p>{loadError}</p><button className="button button--primary" type="button" onClick={() => loadWeek(week)}><RefreshIcon size={18} />重新加载</button></div>
        )}
        {!loading && plan && (
          <div className="planner-layout">
            <section className="planner-column">
              <div className="section-heading"><div><span className="step-badge">1</span><div><h2>安排每日菜品</h2><p>每道菜默认 1 批，即 50 人用量</p></div></div><span className="dish-total">菜品库 {dishes.filter((dish) => dish.active).length} 道</span></div>
              <button className="button button--soft weekly-service-entry" type="button" onClick={() => setServiceSettingsOpen(true)}>
                <CalendarIcon size={19} /><span><strong>本周供餐设置</strong><small>统一调整每天的早餐和中午</small></span><ChevronRightIcon size={19} />
              </button>
              <WeekPlanner
                plan={plan}
                dishes={dishes}
                dailyPurchaseDefaults={dailyPurchaseDefaults}
                dailyPurchaseGroups={aggregation.dailyPurchaseGroups}
                onChange={changePlan}
              />
              <WeeklyExtraPurchases
                plan={plan}
                dishes={dishes}
                ingredients={ingredients}
                dailyPurchaseDefaults={dailyPurchaseDefaults}
                dailyPurchaseGroups={aggregation.dailyPurchaseGroups}
                extraPurchaseItems={aggregation.extraPurchaseItems}
                weeklyDefaultDishIds={WEEKLY_FIXED_PURCHASE_DISH_IDS}
                fixedPurchaseRules={fixedPurchaseRules}
                onOpenFixedSettings={() => setFixedPurchaseSettingsOpen(true)}
                onOpenWeekFixedAdjustments={() => setWeekFixedAdjustmentsOpen(true)}
                onChange={changePlan}
                onOverride={overrideQuantity}
                onResetOverride={resetOverride}
              />
            </section>
            <section className="summary-column">
              <div className="section-heading section-heading--summary"><div><span className="step-badge">3</span><div><h2>核对采购清单</h2><p>点击食材可查看来源，也可单独调整这次采购量</p></div></div></div>
              <PurchaseSummary
                items={items}
                dailyPurchaseGroups={aggregation.dailyPurchaseGroups}
                extraPurchaseItems={aggregation.extraPurchaseItems}
                warnings={warnings}
                snapshot={snapshot}
                outputsStale={outputsStale}
                saveState={saveState}
                confirming={confirming}
                actionBusy={actionBusy}
                onOverride={overrideQuantity}
                onResetOverride={resetOverride}
                onConfirm={confirm}
                onCopy={copyWechat}
                onExcel={downloadExcel}
                onShare={share}
              />
            </section>
          </div>
        )}
      </main>

      <Modal open={libraryOpen} title="菜品库" eyebrow="固定配方 · 50 人 / 批" size="large" onClose={() => setLibraryOpen(false)}>
        <DishLibrary
          dishes={dishes}
          token={token}
          onChange={(nextDishes) => {
            setDishes(nextDishes);
            const nextCatalog = catalogFromDishes(nextDishes);
            setIngredients((current) => Array.from(
              new Map([...current, ...nextCatalog].map((item) => [item.id, item])).values(),
            ));
          }}
          onNotify={notify}
        />
      </Modal>
      {plan && (
        <WeeklyServiceSettings
          open={serviceSettingsOpen}
          plan={plan}
          onClose={() => setServiceSettingsOpen(false)}
          onApply={(next) => {
            changePlan(next);
            notify('本周供餐设置已保存', 'success');
          }}
        />
      )}
      <FixedPurchaseSettings
        open={fixedPurchaseSettingsOpen}
        token={token}
        rules={fixedPurchaseRules}
        ingredients={ingredients}
        dishes={dishes}
        defaultEffectiveFromWeek={fixedPurchaseDefaultEffectiveFromWeek}
        onClose={() => setFixedPurchaseSettingsOpen(false)}
        onRulesChange={(nextRules, revision) => {
          setFixedPurchaseRules(nextRules);
          setPlanWithoutChange(planRef.current ? {
            ...planRef.current,
            ...(snapshot ? { status: 'draft' as const } : {}),
            fixedPurchaseRulesRevision: revision,
          } : planRef.current);
          // A rule effective for the visible week may have re-resolved fixed
          // occurrences and turned an already-confirmed plan back into draft.
          // Reload immediately so the overview, purchase totals and stale
          // output guard all describe the same server state.
          void loadWeek(week);
        }}
        onNotify={notify}
      />
      {plan && (
        <WeeklyFixedPurchaseAdjustments
          open={weekFixedAdjustmentsOpen}
          weekStart={plan.weekStart}
          weekEnd={plan.weekEnd}
          rules={fixedPurchaseRules}
          resolved={plan.resolvedFixedPurchases}
          overrides={plan.fixedPurchaseOverrides ?? []}
          onClose={() => setWeekFixedAdjustmentsOpen(false)}
          onApply={applyFixedPurchaseOverrides}
        />
      )}
      <Modal open={shareOpen} title="采购网页已生成" eyebrow="工作人员只读查看" onClose={() => setShareOpen(false)}>
        <div className="share-panel">
          <span className="share-panel__icon"><LinkIcon size={28} /></span>
          <p>复制下面的链接，通过微信发送给采购工作人员。对方无需登录，只能查看这一版采购单。</p>
          <div className="share-url"><input readOnly value={shareUrl} aria-label="采购单分享链接" /><button type="button" onClick={async () => { await copyText(shareUrl); notify('分享链接已复制', 'success'); }}><CopyIcon size={18} />复制</button></div>
          <div className="inline-alert"><CheckIcon size={17} /><span>重新确认计划不会修改这个链接中的旧版本。</span></div>
        </div>
      </Modal>
      {toast && <Toast key={toast.id} toast={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
